// src/config/verticals.ts
var REGISTRY = /* @__PURE__ */ new Map();
function registerVertical(config) {
  const existing = REGISTRY.get(config.id);
  if (existing && existing !== config) {
    throw new Error(
      `[Square Code] Vertical "${config.id}" is already registered. Each vertical id must be unique.`
    );
  }
  REGISTRY.set(config.id, config);
  return config;
}
function getVertical(id) {
  return REGISTRY.get(id) ?? null;
}
function listVerticals() {
  return Array.from(REGISTRY.values());
}
var DEFAULT_VERTICAL = {
  id: "square-code",
  name: "Square Code",
  description: "Mainframe substrate. OLIVIUM-secured.",
  modules: ["dashboard", "command-center", "olivium", "domains"],
  commandCenter: [
    "overview",
    "requests",
    "files",
    "deliverables",
    "status",
    "comms",
    "billing",
    "workflows"
  ],
  oliviumSections: ["overview", "feed", "sessions", "chain", "seals"],
  oliviumVisibility: "full",
  trustLabels: {
    nominal: "Nominal",
    elevated: "Elevated",
    critical: "Critical"
  }
};
registerVertical(DEFAULT_VERTICAL);

// src/config/verticals/prince-hall.ts
var princeHallConfig = {
  id: "prince-hall",
  name: "Prince Hall",
  description: "Prince Hall lodge operations on the Square Code mainframe.",
  modules: ["dashboard", "command-center", "olivium", "domains"],
  commandCenter: [
    "overview",
    "requests",
    "files",
    "deliverables",
    "status",
    "comms",
    "billing",
    "workflows"
  ],
  oliviumSections: ["overview", "feed", "sessions", "chain", "seals"],
  oliviumVisibility: "full",
  trustLabels: {
    nominal: "Lodge Nominal",
    elevated: "Watch",
    critical: "Tyler Alert"
  },
  roles: [
    { id: "owner", label: "Grand Master", description: "Top-level lodge authority." },
    { id: "admin", label: "Deputy GM", description: "Operational lieutenant." },
    { id: "admin", label: "Secretary", description: "Records and minutes." },
    { id: "member", label: "Brother", description: "Initiated member." },
    { id: "viewer", label: "Visitor", description: "Read-only observer." }
  ],
  branding: {
    wordmark: "PRINCE HALL",
    sublabel: "Lodge Operating Center",
    symbol: "\u25C8"
    // No primary/secondary overrides — keep the OLIVIUM accents.
  }
};
registerVertical(princeHallConfig);

// src/config/verticals/law-pocket.ts
var lawPocketConfig = {
  id: "law-pocket",
  name: "Law Pocket",
  description: "Solo and small-firm legal operations on the Square Code mainframe.",
  modules: ["dashboard", "command-center", "olivium", "domains"],
  commandCenter: [
    "overview",
    "requests",
    "files",
    "deliverables",
    "status",
    "comms",
    "billing",
    "workflows"
  ],
  oliviumSections: ["overview", "feed", "sessions", "chain", "seals"],
  oliviumVisibility: "full",
  trustLabels: {
    nominal: "Privileged Nominal",
    elevated: "Heightened Review",
    critical: "Privilege Breach"
  },
  roles: [
    { id: "owner", label: "Managing Attorney", description: "Firm principal." },
    { id: "admin", label: "Senior Counsel", description: "Operational authority." },
    { id: "admin", label: "Paralegal Lead", description: "Workflow coordinator." },
    { id: "member", label: "Attorney", description: "Practicing attorney." },
    { id: "member", label: "Paralegal", description: "Case support." },
    { id: "viewer", label: "Client", description: "Read-only matter access." }
  ],
  branding: {
    wordmark: "LAW POCKET",
    sublabel: "Practice Operating Center",
    symbol: "\u2696"
  }
};
registerVertical(lawPocketConfig);

// src/config/verticals/kelley-reno.ts
var kelleyRenoConfig = {
  id: "kelley-reno",
  name: "KelleyReno",
  description: "Renovation and construction operations on the Square Code mainframe.",
  modules: ["dashboard", "command-center", "olivium", "domains"],
  commandCenter: [
    "overview",
    "requests",
    "files",
    "deliverables",
    "status",
    "comms",
    "billing",
    "workflows"
  ],
  // Trust layer is enabled but kept compact — operators see chain
  // verification and seals, not the full session forensics view.
  oliviumSections: ["overview", "chain", "seals"],
  oliviumVisibility: "summary",
  trustLabels: {
    nominal: "Site Nominal",
    elevated: "Job Watch",
    critical: "Stop Work"
  },
  roles: [
    { id: "owner", label: "Principal", description: "Company owner." },
    { id: "admin", label: "Project Manager", description: "Job site lead." },
    { id: "member", label: "Foreman", description: "Crew lead." },
    { id: "member", label: "Crew", description: "On-site crew member." },
    { id: "viewer", label: "Homeowner", description: "Read-only client portal." }
  ],
  branding: {
    wordmark: "KELLEY RENO",
    sublabel: "Job Operating Center",
    symbol: "\u2302"
  }
};
registerVertical(kelleyRenoConfig);

// src/config/verticals/noel.ts
var noelConfig = {
  id: "noel",
  name: "Noel",
  description: "Hospitality operations on the Square Code mainframe.",
  modules: ["dashboard", "command-center", "olivium", "domains"],
  commandCenter: [
    "overview",
    "requests",
    "files",
    "deliverables",
    "status",
    "comms",
    "billing",
    "workflows"
  ],
  oliviumSections: ["overview", "sessions"],
  oliviumVisibility: "summary",
  trustLabels: {
    nominal: "House Nominal",
    elevated: "Front Desk Watch",
    critical: "Manager Alert"
  },
  roles: [
    { id: "owner", label: "Proprietor", description: "Property owner." },
    { id: "admin", label: "Manager", description: "Operations manager." },
    { id: "member", label: "Front Desk", description: "Guest-facing staff." },
    { id: "member", label: "Housekeeping", description: "Property staff." },
    { id: "viewer", label: "Guest", description: "Read-only guest portal." }
  ],
  branding: {
    wordmark: "NOEL",
    sublabel: "Hospitality Operating Center",
    symbol: "\u2726"
  }
};
registerVertical(noelConfig);

// src/config/verticals/silver-key.ts
var silverKeyConfig = {
  id: "silver-key",
  name: "Silver Key",
  description: "Estate and high-trust personal asset operations on the Square Code mainframe.",
  modules: ["dashboard", "command-center", "olivium", "domains"],
  commandCenter: [
    "overview",
    "requests",
    "files",
    "deliverables",
    "status",
    "comms",
    "billing",
    "workflows"
  ],
  oliviumSections: ["overview", "feed", "sessions", "chain", "seals"],
  oliviumVisibility: "full",
  trustLabels: {
    nominal: "Vault Nominal",
    elevated: "Vault Watch",
    critical: "Vault Lockdown"
  },
  roles: [
    { id: "owner", label: "Principal", description: "Estate principal." },
    { id: "admin", label: "Trustee", description: "Authorized trustee." },
    { id: "admin", label: "Counsel", description: "Estate counsel." },
    { id: "member", label: "Beneficiary", description: "Named beneficiary." },
    { id: "viewer", label: "Auditor", description: "Read-only auditor seat." }
  ],
  branding: {
    wordmark: "SILVER KEY",
    sublabel: "Vault Operating Center",
    symbol: "\u2736"
  }
};
registerVertical(silverKeyConfig);

export {
  registerVertical,
  getVertical,
  listVerticals,
  DEFAULT_VERTICAL,
  princeHallConfig,
  lawPocketConfig,
  kelleyRenoConfig,
  noelConfig,
  silverKeyConfig
};
